import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { ArrowUp, CheckCircle2, ChevronLeft, ChevronRight, Heart, Loader2, Lock, Pause, Play, UserRound, Volume2, VolumeX } from 'lucide-react';
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BrandLogo } from '../components/BrandLogo';
import { ExportPanel } from '../components/ExportPanel';
import { GalleryCover } from '../components/GalleryCover';
import { GalleryGrid } from '../components/GalleryGrid';
import { PhotoLoadingPanel, PhotoLoadStatus } from '../components/PhotoLoadStatus';
import { VideoGallery } from '../components/VideoGallery';
import { Button } from '../components/ui/Button';
import { Dialog, DialogContent } from '../components/ui/Dialog';
import { Field, Input } from '../components/ui/Field';
import {
  createPublicExport,
  createPublicFavouriteSession,
  getPublicFavouriteSelection,
  getPublicExport,
  getPublicGallery,
  getPublicPhotos,
  getPublicVideoDownloadUrl,
  submitPublicFavouriteSelection,
  unlockPublicGallery,
  type ExportQuality,
  type Photo,
  type PublicFavouriteSelection,
  type Video
} from '../lib/api';
import { coverFocalPointStyle } from '../lib/coverFocalPoint';
import { coverFontOption, normalizeCoverFont } from '../lib/coverFonts';
import {
  GalleryThemeContext,
  galleryColorSchemeVariables,
  galleryStoryStyleOf,
  normalizeGalleryColorScheme,
  normalizeGalleryStyle
} from '../lib/galleryCustomisation';
import { handleMediaError, handleMediaLoad, useMediaRecovery } from '../lib/mediaRecovery';

const PHOTO_PAGE_SIZE = 60;
const ALL_SCENES = '__all__';
const PUBLIC_UNASSIGNED_SCENE = '__unassigned';
const LOCAL_FAVOURITES_SCENE = '__favourites';
const OTHER_SCENE_LABEL = 'Other';
const FAVOURITES_SCENE_LABEL = 'Favourites';
const ALL_PHOTOS_SCENE_LABEL = 'All photos';
const EMPTY_PHOTO_IDS: string[] = [];

interface LocalFavouriteClient {
  clientKey: string;
  name: string;
  email: string;
  photoIds: string[];
  selectionRevision?: number;
  pendingSubmissionId?: string;
}

interface LocalFavouriteState {
  activeClientKey?: string;
  anonymousPhotoIds?: string[];
  clients: Record<string, LocalFavouriteClient>;
}

interface FavouriteSessionState {
  clientKey: string;
  token: string;
}

export function PublicGalleryPage() {
  const { photographerSlug = '', gallerySlug = '' } = useParams();
  const navigate = useNavigate();
  const storageKey = `human-studio.public.${photographerSlug}.${gallerySlug}`;
  const favouritesStorageKey = `human-studio.favourites.${photographerSlug}.${gallerySlug}`;
  const favouriteTokenStorageKey = `${favouritesStorageKey}.session`;
  const [sessionToken, setSessionToken] = useState(() => sessionStorage.getItem(storageKey));
  const [localFavourites, setLocalFavourites] = useState<LocalFavouriteState>(() => readLocalFavouriteState(favouritesStorageKey));
  const [favouriteSession, setFavouriteSession] = useState<FavouriteSessionState | null>(() => readFavouriteSession(favouriteTokenStorageKey));
  const [identityDialogOpen, setIdentityDialogOpen] = useState(false);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [intendedFavouriteId, setIntendedFavouriteId] = useState<string | null>(null);
  const [favouriteMessage, setFavouriteMessage] = useState<string | null>(null);
  const [submittedSelection, setSubmittedSelection] = useState<PublicFavouriteSelection | null>(null);
  const [exportIds, setExportIds] = useState<Partial<Record<ExportQuality, string>>>({});
  const [sceneFilter, setSceneFilter] = useState(ALL_SCENES);
  const previousSceneFilterRef = useRef(ALL_SCENES);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [storyRevealed, setStoryRevealed] = useState(false);
  const [unlockTransitioning, setUnlockTransitioning] = useState(false);
  const storyRef = useRef<HTMLDivElement | null>(null);
  const heroVideoRef = useRef<HTMLVideoElement | null>(null);
  const unlockTransitionTimerRef = useRef<number | null>(null);
  const touchStartYRef = useRef<number | null>(null);
  const [heroPlaying, setHeroPlaying] = useState(false);
  const [heroMuted, setHeroMuted] = useState(true);
  const [loadedCoverUrl, setLoadedCoverUrl] = useState<string | null>(null);
  const [playingVideoUrl, setPlayingVideoUrl] = useState<string | null>(null);
  const reducedMotion = usePrefersReducedMotion();
  const constrainedPlayback = useConstrainedPlayback();
  const recoverPublicMedia = useMediaRecovery([
    ['public-gallery-meta', photographerSlug, gallerySlug],
    ['public-gallery-photos', photographerSlug, gallerySlug, sessionToken]
  ]);

  const metadataQuery = useQuery({
    queryKey: ['public-gallery-meta', photographerSlug, gallerySlug],
    queryFn: () => getPublicGallery(photographerSlug, gallerySlug),
    enabled: Boolean(photographerSlug && gallerySlug)
  });

  const legacyPhotosQuery = useInfiniteQuery({
    queryKey: ['public-gallery-photos', photographerSlug, gallerySlug, sessionToken],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      getPublicPhotos(photographerSlug, gallerySlug, sessionToken!, {
        limit: PHOTO_PAGE_SIZE,
        cursor: pageParam
    }),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: Boolean(sessionToken && metadataQuery.isSuccess && (metadataQuery.data.gallery.scenes?.length ?? 0) === 0),
    refetchInterval: sessionToken ? 12 * 60 * 1000 : false
  });

  const configuredScenes = metadataQuery.data?.gallery.scenes ?? [];
  const sceneQueue = useMemo(
    () => configuredScenes.length > 0 ? [...configuredScenes, PUBLIC_UNASSIGNED_SCENE] : [],
    [configuredScenes]
  );
  const [sceneIndex, setSceneIndex] = useState(0);
  const [sceneBatches, setSceneBatches] = useState<Record<string, Photo[]>>({});
  const [sceneVideos, setSceneVideos] = useState<Video[]>([]);
  const sceneMode = Boolean(sessionToken && metadataQuery.isSuccess && sceneQueue.length > 0);
  const activeScene = sceneQueue[sceneIndex];
  const activeSceneLoaded = Boolean(activeScene && Object.prototype.hasOwnProperty.call(sceneBatches, activeScene));
  const sceneQuery = useQuery({
    queryKey: ['public-gallery-scene-photos', photographerSlug, gallerySlug, sessionToken, activeScene],
    queryFn: () => getPublicPhotos(photographerSlug, gallerySlug, sessionToken!, { scene: activeScene }),
    enabled: sceneMode && Boolean(activeScene),
    refetchInterval: sessionToken ? 12 * 60 * 1000 : false
  });

  useEffect(() => {
    setSceneIndex(0);
    setSceneBatches({});
    setSceneVideos([]);
  }, [photographerSlug, gallerySlug, sessionToken, configuredScenes.join('\u0000')]);

  useEffect(() => {
    if (!sceneMode || !activeScene || !sceneQuery.data) return;
    setSceneBatches((current) => current[activeScene] === sceneQuery.data.photos
      ? current
      : { ...current, [activeScene]: sceneQuery.data.photos });
    if (sceneIndex === 0) {
      setSceneVideos(sceneQuery.data.videos ?? []);
    }
    if (!activeSceneLoaded && sceneIndex < sceneQueue.length - 1) {
      setSceneIndex((current) => current + 1);
    }
  }, [activeScene, activeSceneLoaded, sceneIndex, sceneMode, sceneQuery.data, sceneQueue.length]);

  const webExportQuery = useQuery({
    queryKey: ['public-export', 'web', exportIds.web, sessionToken],
    queryFn: () => getPublicExport(exportIds.web!, sessionToken!),
    enabled: Boolean(exportIds.web && sessionToken),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'READY' || status === 'FAILED' ? false : 5000;
    }
  });

  const originalExportQuery = useQuery({
    queryKey: ['public-export', 'original', exportIds.original, sessionToken],
    queryFn: () => getPublicExport(exportIds.original!, sessionToken!),
    enabled: Boolean(exportIds.original && sessionToken),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'READY' || status === 'FAILED' ? false : 5000;
    }
  });

  const unlockMutation = useMutation({
    mutationFn: (passcode: string) => unlockPublicGallery(photographerSlug, gallerySlug, passcode),
    onSuccess: ({ token }) => {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      sessionStorage.setItem(storageKey, token);
      setSessionToken(token);
      if (reducedMotion) {
        setUnlockTransitioning(false);
        return;
      }
      setUnlockTransitioning(true);
      if (unlockTransitionTimerRef.current !== null) {
        window.clearTimeout(unlockTransitionTimerRef.current);
      }
      unlockTransitionTimerRef.current = window.setTimeout(() => {
        setUnlockTransitioning(false);
        unlockTransitionTimerRef.current = null;
      }, 180);
    }
  });

  const exportMutation = useMutation({
    mutationFn: (quality: ExportQuality) => createPublicExport(photographerSlug, gallerySlug, sessionToken!, quality),
    onSuccess: ({ exportJob }) => setExportIds((current) => ({ ...current, [exportJob.quality]: exportJob.exportId }))
  });

  const activeFavouriteClient = localFavourites.activeClientKey
    ? localFavourites.clients[localFavourites.activeClientKey]
    : undefined;
  const loadedGallery = sceneMode ? sceneQuery.data?.gallery : legacyPhotosQuery.data?.pages[0]?.gallery;
  const favouriteFeatureEnabled = Boolean(
    loadedGallery ? loadedGallery.favourites : metadataQuery.data?.gallery.favourites
  );

  function persistLocalFavourites(next: LocalFavouriteState) {
    localStorage.setItem(favouritesStorageKey, JSON.stringify(next));
    setLocalFavourites(next);
  }

  function updateLocalClient(clientKey: string, update: (client: LocalFavouriteClient) => LocalFavouriteClient) {
    const client = localFavourites.clients[clientKey];
    if (!client) return;
    persistLocalFavourites({
      ...localFavourites,
      clients: { ...localFavourites.clients, [clientKey]: update(client) }
    });
  }

  const favouriteSessionMutation = useMutation({
    mutationFn: (identity: { name: string; email: string }) =>
      createPublicFavouriteSession(photographerSlug, gallerySlug, sessionToken!, identity),
    onSuccess: ({ token, client }) => {
      const existing = localFavourites.clients[client.clientKey];
      const next: LocalFavouriteState = {
        ...localFavourites,
        activeClientKey: client.clientKey,
        clients: {
          ...localFavourites.clients,
          [client.clientKey]: existing
            ? { ...existing, name: client.name, email: client.email }
            : { clientKey: client.clientKey, name: client.name, email: client.email, photoIds: [] }
        }
      };
      persistLocalFavourites(next);
      const nextSession = { clientKey: client.clientKey, token };
      sessionStorage.setItem(favouriteTokenStorageKey, JSON.stringify(nextSession));
      setFavouriteSession(nextSession);
      setSubmittedSelection(null);
      setIdentityDialogOpen(false);
      setFavouriteMessage(null);
    }
  });

  const favouriteSelectionQuery = useQuery({
    queryKey: ['public-favourite-selection', photographerSlug, gallerySlug, favouriteSession?.clientKey, favouriteSession?.token, sessionToken],
    queryFn: () => getPublicFavouriteSelection(
      photographerSlug,
      gallerySlug,
      sessionToken!,
      favouriteSession!.token
    ),
    enabled: Boolean(
      favouriteFeatureEnabled &&
      sessionToken &&
      favouriteSession?.token &&
      activeFavouriteClient?.clientKey === favouriteSession?.clientKey
    ),
    refetchInterval: 60 * 1000
  });

  const submitFavouriteMutation = useMutation({
    mutationFn: (input: { photoIds: string[]; submissionId: string }) =>
      submitPublicFavouriteSelection(
        photographerSlug,
        gallerySlug,
        sessionToken!,
        favouriteSession!.token,
        input
      ),
    onSuccess: ({ selection }) => {
      if (!activeFavouriteClient) return;
      updateLocalClient(activeFavouriteClient.clientKey, (client) => ({
        ...client,
        photoIds: selection.photoIds,
        selectionRevision: selection.revision,
        pendingSubmissionId: undefined
      }));
      setSubmitDialogOpen(false);
      setFavouriteMessage('Selection submitted');
      setSubmittedSelection(selection);
    }
  });

  useEffect(() => {
    const canonicalGallery = metadataQuery.data?.gallery;
    if (!canonicalGallery || canonicalGallery.photographerSlug === photographerSlug) return;
    const canonicalStorageKey = `human-studio.public.${canonicalGallery.photographerSlug}.${canonicalGallery.gallerySlug}`;
    const canonicalFavouritesStorageKey = `human-studio.favourites.${canonicalGallery.photographerSlug}.${canonicalGallery.gallerySlug}`;
    const canonicalFavouriteTokenStorageKey = `${canonicalFavouritesStorageKey}.session`;
    migrateStorageEntry(sessionStorage, storageKey, canonicalStorageKey);
    migrateStorageEntry(localStorage, favouritesStorageKey, canonicalFavouritesStorageKey);
    migrateStorageEntry(sessionStorage, favouriteTokenStorageKey, canonicalFavouriteTokenStorageKey);
    setSessionToken(sessionStorage.getItem(canonicalStorageKey));
    setLocalFavourites(readLocalFavouriteState(canonicalFavouritesStorageKey));
    setFavouriteSession(readFavouriteSession(canonicalFavouriteTokenStorageKey));
    navigate(`/g/${canonicalGallery.photographerSlug}/${canonicalGallery.gallerySlug}`, { replace: true });
  }, [
    metadataQuery.data?.gallery,
    photographerSlug,
    storageKey,
    favouritesStorageKey,
    favouriteTokenStorageKey,
    navigate
  ]);

  useEffect(() => {
    if (legacyPhotosQuery.hasNextPage && !legacyPhotosQuery.isFetchingNextPage) {
      void legacyPhotosQuery.fetchNextPage();
    }
  }, [legacyPhotosQuery.fetchNextPage, legacyPhotosQuery.hasNextPage, legacyPhotosQuery.isFetchingNextPage]);

  const photoPages = legacyPhotosQuery.data?.pages ?? [];
  const gallery = photoPages[0]?.gallery ?? metadataQuery.data?.gallery;
  const normalizedCoverFont = normalizeCoverFont(gallery?.coverFont);
  const coverFontReady = useCoverFontReady(normalizedCoverFont, Boolean(metadataQuery.data?.gallery));
  const galleryColorScheme = normalizeGalleryColorScheme(gallery?.customisation?.colorScheme);
  const galleryStyle = normalizeGalleryStyle(gallery?.customisation?.galleryStyle);
  const storyStyle = galleryStoryStyleOf(galleryStyle);
  const galleryThemeVariables = galleryColorSchemeVariables(galleryColorScheme);
  const photos = sceneMode
    ? sceneQueue.flatMap((scene) => sceneBatches[scene] ?? [])
    : photoPages.flatMap((page) => page.photos);
  const videos = sceneMode ? sceneVideos : photoPages[0]?.videos ?? [];
  const expectedVideoCount = typeof gallery?.videoCount === 'number'
    ? Math.max(0, gallery.videoCount)
    : undefined;
  const firstScene = sceneQueue[0];
  const initialMediaLoaded = sceneMode
    ? Boolean(firstScene && Object.prototype.hasOwnProperty.call(sceneBatches, firstScene))
    : photoPages.length > 0;
  const initialMediaFetching = sceneMode ? sceneQuery.isFetching : legacyPhotosQuery.isFetching;
  const initialMediaFailed = !initialMediaLoaded && !initialMediaFetching && (
    sceneMode ? sceneQuery.isError && sceneIndex === 0 : legacyPhotosQuery.isError
  );
  const showVideoSection = Boolean(
    sessionToken && ((expectedVideoCount ?? 0) > 0 || videos.length > 0)
  );
  const videoRecordsLoading = showVideoSection && !initialMediaLoaded && !initialMediaFailed;
  const scenes = gallery?.scenes ?? [];
  const unassignedPhotoCount = sceneBatches[PUBLIC_UNASSIGNED_SCENE]?.length;
  const coverPhoto = useMemo(() => photos.find((photo) => photo.url), [photos]);
  const heroImageUrl = gallery?.coverUrl ?? coverPhoto?.url;
  const heroFocalStyle = coverFocalPointStyle(gallery?.coverFocalPoint);
  const heroIsVideo = gallery?.coverMediaType === 'video' && Boolean(gallery.coverPlaybackUrl);
  const initialCoverUrl = metadataQuery.data?.gallery.coverUrl;
  const galleryReady = Boolean(metadataQuery.data?.gallery) && coverFontReady && (!initialCoverUrl || loadedCoverUrl === initialCoverUrl);
  const heroVideoVisible = heroIsVideo && playingVideoUrl === gallery?.coverPlaybackUrl;
  const photosLoading = sceneMode
    ? sceneQuery.isLoading || Object.keys(sceneBatches).length < sceneQueue.length
    : legacyPhotosQuery.isLoading || legacyPhotosQuery.isFetchingNextPage;
  const photosLoadedSuccessfully = !photosLoading && (
    sceneMode
      ? sceneQuery.isSuccess && Object.keys(sceneBatches).length === sceneQueue.length
      : legacyPhotosQuery.isSuccess && !legacyPhotosQuery.hasNextPage
  );
  const expectedPhotoCount = gallery ? imageCountOf(gallery) : undefined;
  const favouritesConfig = gallery?.favourites ?? null;
  const anonymousFavouritePhotoIds = localFavourites.anonymousPhotoIds ?? EMPTY_PHOTO_IDS;
  const localFavouritesSceneAvailable = !favouritesConfig && anonymousFavouritePhotoIds.length > 0;
  const anonymousFavouritePhotoIdSet = useMemo(
    () => new Set(anonymousFavouritePhotoIds),
    [anonymousFavouritePhotoIds]
  );
  const filteredPhotos = useMemo(
    () => filterPhotosByScene(photos, sceneFilter, anonymousFavouritePhotoIdSet),
    [anonymousFavouritePhotoIdSet, photos, sceneFilter]
  );
  const loadedPhotoIdKey = photos.map((photo) => photo.photoId).join('\u0000');
  const serverFavouriteSelection = favouriteSelectionQuery.data?.selection ?? null;
  const favouriteSelection = serverFavouriteSelection && (
    !submittedSelection || serverFavouriteSelection.revision >= submittedSelection.revision
  )
    ? serverFavouriteSelection
    : submittedSelection;
  const favouritePhotoIds = useMemo(
    () => new Set(favouritesConfig ? (activeFavouriteClient?.photoIds ?? []) : anonymousFavouritePhotoIds),
    [activeFavouriteClient?.photoIds, anonymousFavouritePhotoIds, favouritesConfig]
  );
  const favouritesLocked = favouriteSelection?.status === 'SUBMITTED';
  const favouritesControlsLocked = favouritesLocked || Boolean(
    activeFavouriteClient && (!favouriteSession || favouriteSelectionQuery.isLoading || !favouriteSelectionQuery.isFetched)
  );
  const favouritesLimitReached = Boolean(
    favouritesConfig && activeFavouriteClient && activeFavouriteClient.photoIds.length >= favouritesConfig.limit
  );

  useEffect(() => {
    if (
      !sessionToken ||
      !(sceneMode ? sceneQuery.isSuccess : legacyPhotosQuery.isSuccess) ||
      !favouritesConfig ||
      !activeFavouriteClient ||
      favouriteSession?.clientKey === activeFavouriteClient.clientKey ||
      favouriteSessionMutation.isPending ||
      favouriteSessionMutation.isError
    ) return;
    favouriteSessionMutation.mutate({ name: activeFavouriteClient.name, email: activeFavouriteClient.email });
  }, [
    sessionToken,
    sceneMode,
    sceneQuery.isSuccess,
    legacyPhotosQuery.isSuccess,
    favouritesConfig,
    activeFavouriteClient?.clientKey,
    favouriteSession?.clientKey,
    favouriteSessionMutation.isPending,
    favouriteSessionMutation.isError
  ]);

  useEffect(() => {
    if (!isAuthenticationError(sceneMode ? sceneQuery.error : legacyPhotosQuery.error)) return;
    sessionStorage.removeItem(storageKey);
    sessionStorage.removeItem(favouriteTokenStorageKey);
    setSessionToken(null);
    setFavouriteSession(null);
    setSubmittedSelection(null);
  }, [sceneMode, sceneQuery.error, legacyPhotosQuery.error, storageKey, favouriteTokenStorageKey]);

  useEffect(() => {
    if (!isAuthenticationError(favouriteSelectionQuery.error)) return;
    sessionStorage.removeItem(favouriteTokenStorageKey);
    setFavouriteSession(null);
    setSubmittedSelection(null);
  }, [favouriteSelectionQuery.error, favouriteTokenStorageKey]);

  useEffect(() => {
    if (favouritesConfig || !photosLoadedSuccessfully || anonymousFavouritePhotoIds.length === 0) return;
    const loadedPhotoIds = new Set(photos.map((photo) => photo.photoId));
    const validPhotoIds = anonymousFavouritePhotoIds.filter((photoId) => loadedPhotoIds.has(photoId));
    if (sameStringArray(validPhotoIds, anonymousFavouritePhotoIds)) return;
    persistLocalFavourites({ ...localFavourites, anonymousPhotoIds: validPhotoIds });
  }, [anonymousFavouritePhotoIds, favouritesConfig, loadedPhotoIdKey, photosLoadedSuccessfully]);

  useEffect(() => {
    if (
      !activeFavouriteClient ||
      favouriteSession?.clientKey !== activeFavouriteClient.clientKey ||
      !favouriteSelectionQuery.isFetched
    ) return;
    let nextClient = activeFavouriteClient;
    let changed = false;
    let consumeAnonymousFavourites = false;
    let addedAnonymousCount = 0;
    let omittedAnonymousCount = 0;
    let intendedFavouriteRejected = false;
    if (favouriteSelection?.status === 'SUBMITTED') {
      if (
        nextClient.selectionRevision !== favouriteSelection.revision ||
        !sameStringArray(nextClient.photoIds, favouriteSelection.photoIds)
      ) {
        nextClient = {
          ...nextClient,
          photoIds: favouriteSelection.photoIds,
          selectionRevision: favouriteSelection.revision,
          pendingSubmissionId: undefined
        };
        changed = true;
      }
    } else if (favouriteSelection?.status === 'REOPENED' && nextClient.selectionRevision !== favouriteSelection.revision) {
      nextClient = {
        ...nextClient,
        photoIds: favouriteSelection.photoIds,
        selectionRevision: favouriteSelection.revision,
        pendingSubmissionId: undefined
      };
      changed = true;
    }

    if (
      favouritesConfig &&
      favouriteSelectionQuery.isSuccess &&
      photosLoadedSuccessfully &&
      anonymousFavouritePhotoIds.length > 0 &&
      favouriteSelection?.status !== 'SUBMITTED'
    ) {
      const loadedPhotoIds = new Set(photos.map((photo) => photo.photoId));
      const anonymousCandidates = anonymousFavouritePhotoIds.filter(
        (photoId) => loadedPhotoIds.has(photoId) && !nextClient.photoIds.includes(photoId)
      );
      const availableSlots = Math.max(0, favouritesConfig.limit - nextClient.photoIds.length);
      const photoIdsToAdd = anonymousCandidates.slice(0, availableSlots);
      addedAnonymousCount = photoIdsToAdd.length;
      omittedAnonymousCount = anonymousCandidates.length - photoIdsToAdd.length;
      if (photoIdsToAdd.length > 0) {
        nextClient = { ...nextClient, photoIds: [...nextClient.photoIds, ...photoIdsToAdd], pendingSubmissionId: undefined };
        changed = true;
      }
      consumeAnonymousFavourites = true;
    }

    if (intendedFavouriteId && favouriteSelection?.status !== 'SUBMITTED') {
      if (!nextClient.photoIds.includes(intendedFavouriteId)) {
        if (favouritesConfig && nextClient.photoIds.length < favouritesConfig.limit) {
          nextClient = { ...nextClient, photoIds: [...nextClient.photoIds, intendedFavouriteId] };
          changed = true;
        } else {
          intendedFavouriteRejected = true;
        }
      }
      setIntendedFavouriteId(null);
    }

    if (consumeAnonymousFavourites) {
      const rejectedChoices = [
        omittedAnonymousCount > 0
          ? `${omittedAnonymousCount} saved ${omittedAnonymousCount === 1 ? 'favourite' : 'favourites'}`
          : null,
        intendedFavouriteRejected ? 'your new choice' : null
      ].filter((value): value is string => Boolean(value));
      const addedMessage = addedAnonymousCount > 0
        ? `Added ${addedAnonymousCount} saved ${addedAnonymousCount === 1 ? 'favourite' : 'favourites'} to this selection.`
        : '';
      const rejectedMessage = rejectedChoices.length > 0
        ? `${rejectedChoices.join(' and ')} could not fit within the ${favouritesConfig!.limit}-photo limit.`
        : '';
      setFavouriteMessage([addedMessage, rejectedMessage].filter(Boolean).join(' '));
    } else if (intendedFavouriteRejected) {
      setFavouriteMessage(`You can choose up to ${favouritesConfig?.limit ?? 0} photos`);
    }

    if (changed || consumeAnonymousFavourites) {
      persistLocalFavourites({
        ...localFavourites,
        ...(consumeAnonymousFavourites ? { anonymousPhotoIds: [] } : {}),
        clients: { ...localFavourites.clients, [nextClient.clientKey]: nextClient }
      });
    }
  }, [
    favouriteSelectionQuery.isFetched,
    favouriteSelectionQuery.isSuccess,
    favouriteSelection?.revision,
    favouriteSelection?.status,
    favouriteSelection?.photoIds,
    activeFavouriteClient?.clientKey,
    activeFavouriteClient?.photoIds,
    favouriteSession?.clientKey,
    intendedFavouriteId,
    anonymousFavouritePhotoIds,
    favouritesConfig?.limit,
    loadedPhotoIdKey,
    photosLoadedSuccessfully
  ]);

  useEffect(() => {
    if (!initialCoverUrl) {
      return;
    }
    let cancelled = false;
    const cover = new Image();
    const finish = () => {
      if (!cancelled) {
        setLoadedCoverUrl(initialCoverUrl);
      }
    };
    cover.onload = finish;
    cover.onerror = finish;
    cover.src = initialCoverUrl;
    return () => {
      cancelled = true;
    };
  }, [initialCoverUrl]);

  useEffect(() => {
    setHeroPlaying(false);
    setPlayingVideoUrl(null);
  }, [gallery?.coverPlaybackUrl]);

  useEffect(() => {
    if (sceneFilter !== LOCAL_FAVOURITES_SCENE) {
      previousSceneFilterRef.current = sceneFilter;
    }
  }, [sceneFilter]);

  useEffect(() => {
    const otherSceneAvailable = unassignedPhotoCount !== undefined && unassignedPhotoCount > 0;
    const defaultFilter = scenes[0] ?? ALL_SCENES;
    if (sceneFilter === LOCAL_FAVOURITES_SCENE) {
      if (localFavouritesSceneAvailable) return;
      const previousFilter = previousSceneFilterRef.current;
      const previousFilterAvailable = previousFilter === ALL_SCENES
        ? scenes.length === 0
        : previousFilter === PUBLIC_UNASSIGNED_SCENE
          ? otherSceneAvailable
          : scenes.includes(previousFilter);
      setSceneFilter(previousFilterAvailable ? previousFilter : defaultFilter);
      return;
    }
    if (scenes.length > 0 && (
      sceneFilter === ALL_SCENES ||
      (sceneFilter === PUBLIC_UNASSIGNED_SCENE && !otherSceneAvailable) ||
      (sceneFilter !== PUBLIC_UNASSIGNED_SCENE && !scenes.includes(sceneFilter))
    )) {
      setSceneFilter(defaultFilter);
      return;
    }
    if (scenes.length === 0 && sceneFilter !== ALL_SCENES) {
      setSceneFilter(ALL_SCENES);
    }
  }, [localFavouritesSceneAvailable, sceneFilter, scenes, unassignedPhotoCount]);

  useEffect(() => {
    if (!sessionToken) {
      setShowBackToTop(false);
      return;
    }

    function handleScroll() {
      const documentElement = document.documentElement;
      const scrollBottom = window.scrollY + window.innerHeight;
      const hasScrollablePhotos = documentElement.scrollHeight > window.innerHeight + 160;
      setShowBackToTop(hasScrollablePhotos && scrollBottom >= documentElement.scrollHeight - 120);
    }

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [sessionToken, filteredPhotos.length]);

  useEffect(() => () => {
    if (unlockTransitionTimerRef.current !== null) {
      window.clearTimeout(unlockTransitionTimerRef.current);
    }
  }, []);

  useEffect(() => {
    setStoryRevealed(false);
  }, [photographerSlug, gallerySlug]);

  useEffect(() => {
    if (sessionToken) return;
    setStoryRevealed(false);
    setUnlockTransitioning(false);
  }, [sessionToken]);

  const revealStory = useCallback(() => {
    if (!sessionToken || !storyRef.current) return;
    if (!storyRevealed) setStoryRevealed(true);
    if (typeof storyRef.current.scrollIntoView === 'function') {
      storyRef.current.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
    }
  }, [reducedMotion, sessionToken, storyRevealed]);

  useEffect(() => {
    if (!sessionToken || storyRevealed || unlockTransitioning) return;

    const atTop = () => window.scrollY <= 2;
    const handleWheel = (event: WheelEvent) => {
      if (event.deltaY <= 4 || !atTop()) return;
      event.preventDefault();
      revealStory();
    };
    const handleTouchStart = (event: TouchEvent) => {
      touchStartYRef.current = atTop() && event.touches.length === 1
        ? event.touches[0].clientY
        : null;
    };
    const handleTouchMove = (event: TouchEvent) => {
      const startY = touchStartYRef.current;
      if (startY === null || event.touches.length !== 1 || startY - event.touches[0].clientY <= 8) return;
      event.preventDefault();
      touchStartYRef.current = null;
      revealStory();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.defaultPrevented ||
        event.repeat ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        !atTop() ||
        !['ArrowDown', 'PageDown', ' ', 'Spacebar'].includes(event.key)
      ) return;
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (target?.closest('button, input, select, textarea, a, [contenteditable="true"]')) return;
      event.preventDefault();
      revealStory();
    };
    const handleScroll = () => {
      if (window.scrollY > 8) setStoryRevealed(true);
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [revealStory, sessionToken, storyRevealed, unlockTransitioning]);

  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function changeSceneFilter(filter: string) {
    if (filter !== LOCAL_FAVOURITES_SCENE) {
      previousSceneFilterRef.current = filter;
    }
    setSceneFilter(filter);
  }

  async function downloadVideo(video: Video) {
    if (!sessionToken) {
      return;
    }
    const { url } = await getPublicVideoDownloadUrl(photographerSlug, gallerySlug, video.photoId, sessionToken);
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  function retryInitialMedia() {
    if (sceneMode) {
      void sceneQuery.refetch();
    } else {
      void legacyPhotosQuery.refetch();
    }
  }

  async function toggleHeroPlayback() {
    const video = heroVideoRef.current;
    if (!video) {
      return;
    }
    if (video.paused) {
      await video.play();
    } else {
      video.pause();
    }
  }

  function toggleFavourite(photoId: string) {
    if (!favouritesConfig) {
      const nextPhotoIds = anonymousFavouritePhotoIds.includes(photoId)
        ? anonymousFavouritePhotoIds.filter((candidate) => candidate !== photoId)
        : [...anonymousFavouritePhotoIds, photoId];
      persistLocalFavourites({ ...localFavourites, anonymousPhotoIds: nextPhotoIds });
      return;
    }
    if (favouritesLocked) return;
    setFavouriteMessage(null);
    if (!activeFavouriteClient) {
      setIntendedFavouriteId(photoId);
      setIdentityDialogOpen(true);
      return;
    }
    if (!favouriteSession || favouriteSession.clientKey !== activeFavouriteClient.clientKey) {
      setIntendedFavouriteId(photoId);
      if (!favouriteSessionMutation.isPending) {
        favouriteSessionMutation.mutate({ name: activeFavouriteClient.name, email: activeFavouriteClient.email });
      }
      return;
    }
    updateLocalClient(activeFavouriteClient.clientKey, (client) => {
      if (client.photoIds.includes(photoId)) {
        return { ...client, photoIds: client.photoIds.filter((candidate) => candidate !== photoId), pendingSubmissionId: undefined };
      }
      if (client.photoIds.length >= favouritesConfig.limit) {
        setFavouriteMessage(`You can choose up to ${favouritesConfig.limit} photos`);
        return client;
      }
      return { ...client, photoIds: [...client.photoIds, photoId], pendingSubmissionId: undefined };
    });
  }

  function submitFavouriteDraft() {
    if (!activeFavouriteClient || !favouriteSession || activeFavouriteClient.photoIds.length === 0) return;
    const submissionId = activeFavouriteClient.pendingSubmissionId ?? createSubmissionId();
    if (!activeFavouriteClient.pendingSubmissionId) {
      updateLocalClient(activeFavouriteClient.clientKey, (client) => ({ ...client, pendingSubmissionId: submissionId }));
    }
    submitFavouriteMutation.mutate({ photoIds: activeFavouriteClient.photoIds, submissionId });
  }

  function switchFavouriteClient() {
    setIntendedFavouriteId(null);
    setIdentityDialogOpen(true);
  }

  const unlocked = Boolean(sessionToken);
  const showPasscodeGate = !unlocked || unlockTransitioning;
  const heroVisualUnlocked = unlocked && !unlockTransitioning;

  if (!metadataQuery.data?.gallery && metadataQuery.isError) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-white px-6 text-ink">
        <div className="grid max-w-sm gap-3 text-center">
          <p className="font-medium">Could not load this gallery</p>
          <p className="text-sm text-ink/70">Please check your connection and try again.</p>
          <Button type="button" variant="secondary" onClick={() => void metadataQuery.refetch()}>Try again</Button>
        </div>
      </main>
    );
  }

  if (!galleryReady) {
    return <GalleryLoadingScreen />;
  }

  const videoSection = showVideoSection ? (
    <section
      className="client-gallery-surface mx-auto grid w-full max-w-[1260px] gap-5 px-3 py-8 sm:px-5"
      data-gallery-surface="videos"
      aria-labelledby="public-videos-heading"
      aria-busy={videoRecordsLoading}
    >
      <div className="flex min-h-10 items-center justify-between gap-4">
        <h2 id="public-videos-heading" className="text-2xl font-light text-ink sm:text-3xl">Videos</h2>
        {initialMediaFailed ? (
          <Button type="button" variant="ghost" size="sm" onClick={retryInitialMedia}>Retry videos</Button>
        ) : null}
      </div>
      <VideoGallery
        videos={videos}
        galleryStyle={storyStyle}
        placeholderCount={expectedVideoCount ?? videos.length}
        isLoading={videoRecordsLoading}
        loadingFailed={initialMediaFailed}
        showDetails={false}
        onDownload={(video) => void downloadVideo(video)}
        onMediaError={recoverPublicMedia}
      />
    </section>
  ) : null;

  const storyControls = (
    <>
      {sessionToken && (scenes.length > 0 || localFavouritesSceneAvailable) ? (
        <SceneTabs
          scenes={scenes}
          unassignedPhotoCount={unassignedPhotoCount}
          showAllPhotos={scenes.length === 0 && localFavouritesSceneAvailable}
          showLocalFavourites={localFavouritesSceneAvailable}
          sceneFilter={sceneFilter}
          onFilterChange={changeSceneFilter}
        />
      ) : null}

      {sessionToken && (photosLoading || (expectedPhotoCount ?? photos.length) > 0 || Boolean(favouritesConfig)) ? (
        <section className="client-gallery-band px-3 py-5 sm:px-5 sm:py-6" data-gallery-surface="proofing" aria-label="Gallery downloads and proofing">
          <div className="mx-auto grid w-full max-w-[1260px] gap-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-5">
              <PhotoLoadStatus
                loadedCount={photos.length}
                expectedCount={expectedPhotoCount}
                isLoading={photosLoading}
                showProgress
                className="client-gallery-progress w-full sm:flex-1"
              />
              {(expectedPhotoCount ?? photos.length) > 0 ? (
                <ExportPanel
                  variant="toolbar"
                  exportJobs={{
                    web: webExportQuery.data ?? (exportMutation.data?.exportJob.quality === 'web' ? exportMutation.data.exportJob : null),
                    original: originalExportQuery.data ?? (exportMutation.data?.exportJob.quality === 'original' ? exportMutation.data.exportJob : null)
                  }}
                  creatingQuality={exportMutation.isPending ? exportMutation.variables : null}
                  onCreate={(quality) => exportMutation.mutate(quality)}
                />
              ) : null}
            </div>

            {favouritesConfig ? (
              <FavouriteToolbar
                client={activeFavouriteClient}
                count={activeFavouriteClient?.photoIds.length ?? 0}
                limit={favouritesConfig.limit}
                selection={favouriteSelection}
                isConnecting={favouriteSessionMutation.isPending || favouriteSelectionQuery.isLoading}
                isSubmitting={submitFavouriteMutation.isPending}
                hasSession={favouriteSession?.clientKey === activeFavouriteClient?.clientKey}
                message={favouriteMessage}
                error={favouriteSessionMutation.error ?? favouriteSelectionQuery.error ?? submitFavouriteMutation.error}
                onIdentify={() => setIdentityDialogOpen(true)}
                onSwitchClient={switchFavouriteClient}
                onReconnect={() => {
                  if (!activeFavouriteClient) return;
                  favouriteSessionMutation.reset();
                  favouriteSessionMutation.mutate({ name: activeFavouriteClient.name, email: activeFavouriteClient.email });
                }}
                onSubmit={() => setSubmitDialogOpen(true)}
              />
            ) : null}
          </div>
        </section>
      ) : null}
    </>
  );

  const photoSection = (
    <section className="client-gallery-surface grid w-full flex-1 gap-6 px-3 py-6 sm:px-5 sm:py-7" data-gallery-surface="content">
      {filteredPhotos.length === 0 && photosLoading ? (
        <PhotoLoadingPanel loadedCount={photos.length} expectedCount={expectedPhotoCount} showReadyCount={false} />
      ) : filteredPhotos.length === 0 ? (
        <SceneEmptyState sceneFilter={sceneFilter} />
      ) : (
        <GalleryGrid
          photos={filteredPhotos}
          galleryStyle={storyStyle}
          showPhotoDetails={false}
          variant="client"
          favouritePhotoIds={favouritePhotoIds}
          onToggleFavourite={toggleFavourite}
          favouritesLocked={Boolean(favouritesConfig) && favouritesControlsLocked}
          favouritesLimitReached={Boolean(favouritesConfig) && favouritesLimitReached}
          onMediaError={recoverPublicMedia}
        />
      )}
    </section>
  );

  return (
    <GalleryThemeContext.Provider value={galleryThemeVariables}>
      <main
        className="client-gallery flex min-h-screen flex-col text-ink"
        data-gallery-color-scheme={galleryColorScheme}
        data-gallery-style={galleryStyle}
        style={galleryThemeVariables}
      >
      <GalleryCover
        title={gallery?.title ?? 'Gallery'}
        description={gallery?.description}
        eventDate={gallery?.eventDate}
        coverFont={normalizedCoverFont}
        galleryStyle={galleryStyle}
        state={heroVisualUnlocked ? 'unlocked' : 'locked'}
        imageUrl={heroImageUrl}
        focalPoint={gallery?.coverFocalPoint}
        onImageLoad={handleMediaLoad}
        onImageError={(event) => handleMediaError(event, recoverPublicMedia)}
        video={heroIsVideo ? (
          <video
            key={gallery?.coverPlaybackUrl}
            ref={heroVideoRef}
            src={gallery?.coverPlaybackUrl}
            poster={heroImageUrl}
            autoPlay={!constrainedPlayback}
            muted={heroMuted}
            loop
            playsInline
            preload={constrainedPlayback ? 'none' : 'auto'}
            onPlaying={() => {
              setHeroPlaying(true);
              setPlayingVideoUrl(gallery?.coverPlaybackUrl ?? null);
            }}
            onPause={() => setHeroPlaying(false)}
            onError={() => setPlayingVideoUrl(null)}
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-out ${heroVideoVisible ? 'opacity-100' : 'opacity-0'}`}
            style={heroFocalStyle}
          />
        ) : null}
        action={showPasscodeGate ? (
            <PasscodeGate
              isPending={unlockMutation.isPending || unlockTransitioning}
              exiting={unlockTransitioning}
              error={unlockMutation.error}
              onSubmit={(passcode) => unlockMutation.mutate(passcode)}
            />
          ) : (
            <button type="button" className="gallery-hero-cta gallery-hero-cta-unlocked" onClick={revealStory}>
              View our story
            </button>
          )}
        controls={heroIsVideo ? (
          <div className="absolute bottom-4 right-4 z-20 flex gap-2">
            <Button type="button" variant="secondary" size="icon" onClick={() => void toggleHeroPlayback()} aria-label={heroPlaying ? 'Pause cover video' : 'Play cover video'}>
              {heroPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="icon"
              onClick={() => setHeroMuted((current) => !current)}
              aria-label={heroMuted ? 'Unmute cover video' : 'Mute cover video'}
            >
              {heroMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </Button>
          </div>
        ) : null}
      />

      {unlocked ? (
        <div
          ref={storyRef}
          className="public-gallery-story"
          data-gallery-story-style={storyStyle}
          data-story-revealed={storyRevealed}
          onFocusCapture={() => {
            if (!storyRevealed) revealStory();
          }}
        >
      {storyStyle === 'filmstrip' ? (
        <>
          {storyControls}
          {photoSection}
          {videoSection}
        </>
      ) : (
        <>
          {videoSection}
          {storyControls}
          {photoSection}
        </>
      )}

      <footer className="client-gallery-band px-5 py-10 sm:px-8 sm:py-12" data-gallery-surface="footer" aria-label="Storyly footer">
        <div className="mx-auto flex w-full max-w-[1260px] flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
          <div className="grid gap-3">
            <BrandLogo variant="mark" className="h-9 brightness-0 invert" />
            <p className="text-xs uppercase tracking-[0.18em] opacity-70">Private gallery experience</p>
          </div>
          <p className="text-xs opacity-65">© {new Date().getFullYear()} Storyly</p>
        </div>
      </footer>
        </div>
      ) : null}

      {showBackToTop ? (
        <button type="button" className="client-gallery-floating-control fixed bottom-5 right-5 z-40 inline-flex h-12 w-12 items-center justify-center rounded-full shadow-soft transition" onClick={scrollToTop} aria-label="Back to top">
          <ArrowUp className="h-5 w-5" />
        </button>
      ) : null}

      {favouritesConfig ? (
        <>
          <FavouriteIdentityDialog
            open={identityDialogOpen}
            isPending={favouriteSessionMutation.isPending}
            error={favouriteSessionMutation.error}
            onOpenChange={(open) => {
              setIdentityDialogOpen(open);
              if (!open) setIntendedFavouriteId(null);
            }}
            onSubmit={(identity) => favouriteSessionMutation.mutate(identity)}
          />

          <Dialog open={submitDialogOpen} onOpenChange={setSubmitDialogOpen}>
            <DialogContent
              title="Submit your selection?"
              description={`You’re submitting ${activeFavouriteClient?.photoIds.length ?? 0} photos. Your selection will be locked until the photographer reopens it.`}
            >
              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <Button type="button" variant="secondary" onClick={() => setSubmitDialogOpen(false)}>Keep editing</Button>
                <Button type="button" onClick={submitFavouriteDraft} disabled={submitFavouriteMutation.isPending}>
                  {submitFavouriteMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  <span>{submitFavouriteMutation.isPending ? 'Submitting' : 'Submit selection'}</span>
                </Button>
              </div>
              {submitFavouriteMutation.error ? <p className="mt-3 text-sm text-clay">{submitFavouriteMutation.error.message}</p> : null}
            </DialogContent>
          </Dialog>
        </>
      ) : null}
      </main>
    </GalleryThemeContext.Provider>
  );
}

function GalleryLoadingScreen() {
  return (
    <main className="flex min-h-screen items-center justify-center bg-white px-6 text-ink" aria-busy="true">
      <div className="flex flex-col items-center gap-4 text-center" role="status" aria-live="polite">
        <Loader2 className="h-7 w-7 animate-spin text-sea" aria-hidden="true" />
        <p className="text-sm font-medium tracking-wide text-ink/70">Loading gallery</p>
      </div>
    </main>
  );
}

function FavouriteToolbar({
  client,
  count,
  limit,
  selection,
  isConnecting,
  isSubmitting,
  hasSession,
  message,
  error,
  onIdentify,
  onSwitchClient,
  onReconnect,
  onSubmit
}: {
  client?: LocalFavouriteClient;
  count: number;
  limit: number;
  selection: PublicFavouriteSelection | null;
  isConnecting: boolean;
  isSubmitting: boolean;
  hasSession: boolean;
  message: string | null;
  error: Error | null;
  onIdentify: () => void;
  onSwitchClient: () => void;
  onReconnect: () => void;
  onSubmit: () => void;
}) {
  const locked = selection?.status === 'SUBMITTED';
  return (
    <section className="client-gallery-proofing flex w-full flex-col gap-3 border-t border-white/20 pt-4 sm:flex-row sm:items-center sm:justify-between" aria-label="Favourite selection">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <Heart className="h-5 w-5" fill={count > 0 ? 'currentColor' : 'none'} />
          <p className="font-medium">
            {client ? `${count} / ${limit} favourites` : `Choose up to ${limit} favourites`}
          </p>
        </div>
        {client ? (
          <p className="mt-1 text-sm opacity-75">
            {locked
              ? `Submitted by ${client.name} · ${formatFavouriteTime(selection.submittedAt)}`
              : selection?.status === 'REOPENED'
                ? `Reopened for ${client.name}. Make changes, then submit again.`
                : `Selecting as ${client.name} · changes stay on this device until submitted.`}
          </p>
        ) : (
          <p className="mt-1 text-sm opacity-75">Tap a heart on any photo to begin.</p>
        )}
        {message ? <p className="mt-1 text-sm font-medium text-sea" role="status">{message}</p> : null}
        {error ? <p className="mt-1 text-sm text-clay">{error.message}</p> : null}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {client ? (
          <Button type="button" variant="ghost" size="sm" onClick={onSwitchClient}>
            <UserRound className="h-4 w-4" /> Switch client
          </Button>
        ) : (
          <Button type="button" variant="secondary" onClick={onIdentify}>
            <UserRound className="h-4 w-4" /> Start selection
          </Button>
        )}
        {client && !hasSession ? (
          <Button type="button" variant="secondary" onClick={onReconnect} disabled={isConnecting}>
            {isConnecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Heart className="h-4 w-4" />}
            Reconnect selection
          </Button>
        ) : null}
        {client && hasSession && !locked ? (
          <Button type="button" onClick={onSubmit} disabled={count === 0 || count > limit || isConnecting || isSubmitting}>
            {isConnecting || isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            Submit selection
          </Button>
        ) : null}
        {locked ? <span className="inline-flex items-center gap-2 rounded-full border border-white/25 px-3 py-2 text-sm font-medium"><Lock className="h-4 w-4" /> Locked</span> : null}
      </div>
    </section>
  );
}

function FavouriteIdentityDialog({
  open,
  isPending,
  error,
  onOpenChange,
  onSubmit
}: {
  open: boolean;
  isPending: boolean;
  error: Error | null;
  onOpenChange: (open: boolean) => void;
  onSubmit: (identity: { name: string; email: string }) => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  useEffect(() => {
    if (!open) {
      setName('');
      setEmail('');
    }
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="Who is choosing favourites?" description="Your name and email keep each client’s draft separate on this device.">
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit({ name, email });
          }}
        >
          <Field label="Name"><Input value={name} maxLength={100} autoComplete="name" required autoFocus onChange={(event) => setName(event.target.value)} /></Field>
          <Field label="Email"><Input type="email" value={email} autoComplete="email" required onChange={(event) => setEmail(event.target.value)} /></Field>
          {error ? <p className="text-sm text-clay">{error.message}</p> : null}
          <div className="flex justify-end">
            <Button disabled={isPending}>
              {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Heart className="h-4 w-4" />}
              <span>{isPending ? 'Starting' : 'Start choosing'}</span>
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function formatFavouriteTime(value: string): string {
  return new Intl.DateTimeFormat('en-ZA', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
}

function imageCountOf(gallery: { photoCount: number; imageCount?: number; videoCount?: number }): number {
  return gallery.imageCount ?? Math.max(0, gallery.photoCount - (gallery.videoCount ?? 0));
}

function usePrefersReducedMotion(): boolean {
  const [reducedMotion, setReducedMotion] = useState(() => (
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ));
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(media.matches);
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  return reducedMotion;
}

function useCoverFontReady(value: unknown, enabled: boolean): boolean {
  const normalizedFont = normalizeCoverFont(value);
  const [readyFont, setReadyFont] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let active = true;
    const fontSet = document.fonts;
    if (!fontSet || typeof fontSet.load !== 'function') {
      setReadyFont(normalizedFont);
      return;
    }

    const option = coverFontOption(normalizedFont);
    const primaryFamily = option.family.split(',')[0].trim();
    const descriptors = Array.from(new Set([option.titleWeight, option.descriptionWeight]))
      .map((weight) => `${weight} 1em ${primaryFamily}`);
    void Promise.all(descriptors.map((descriptor) => fontSet.load(descriptor)))
      .catch(() => undefined)
      .then(() => {
        if (active) setReadyFont(normalizedFont);
      });

    return () => {
      active = false;
    };
  }, [enabled, normalizedFont]);

  return enabled && readyFont === normalizedFont;
}

function useConstrainedPlayback(): boolean {
  const [constrained, setConstrained] = useState(() => playbackIsConstrained());
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return;
    }
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setConstrained(playbackIsConstrained());
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  return constrained;
}

function playbackIsConstrained(): boolean {
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
  const reducedMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return reducedMotion || Boolean(connection?.saveData);
}

function SceneTabs({
  scenes,
  unassignedPhotoCount,
  showAllPhotos,
  showLocalFavourites,
  sceneFilter,
  onFilterChange
}: {
  scenes: string[];
  unassignedPhotoCount?: number;
  showAllPhotos: boolean;
  showLocalFavourites: boolean;
  sceneFilter: string;
  onFilterChange: (filter: string) => void;
}) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef(new Map<string, HTMLButtonElement>());
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const hasSceneOverflow = canScrollLeft || canScrollRight;
  const hasOtherScene = unassignedPhotoCount !== undefined && unassignedPhotoCount > 0;
  const tabs = useMemo(
    () => [
      ...(showAllPhotos ? [ALL_SCENES] : []),
      ...scenes,
      ...(hasOtherScene ? [PUBLIC_UNASSIGNED_SCENE] : []),
      ...(showLocalFavourites ? [LOCAL_FAVOURITES_SCENE] : [])
    ],
    [hasOtherScene, scenes, showAllPhotos, showLocalFavourites]
  );

  const updateScrollControls = useCallback(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    const maxScrollLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    setCanScrollLeft(scroller.scrollLeft > 1);
    setCanScrollRight(scroller.scrollLeft < maxScrollLeft - 1);
  }, []);

  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    updateScrollControls();
    scroller.addEventListener('scroll', updateScrollControls, { passive: true });
    window.addEventListener('resize', updateScrollControls);
    const resizeObserver = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(updateScrollControls);
    resizeObserver?.observe(scroller);
    tabRefs.current.forEach((tab) => resizeObserver?.observe(tab));
    return () => {
      scroller.removeEventListener('scroll', updateScrollControls);
      window.removeEventListener('resize', updateScrollControls);
      resizeObserver?.disconnect();
    };
  }, [tabs, updateScrollControls]);

  function scrollScenes(direction: -1 | 1) {
    const scroller = scrollRef.current;
    if (!scroller) return;
    const maxScrollLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const left = Math.max(0, Math.min(maxScrollLeft, scroller.scrollLeft + direction * scroller.clientWidth * 0.75));
    scroller.scrollTo({ left, behavior: 'smooth' });
  }

  function revealSelectedAndNext(filter: string) {
    const scroller = scrollRef.current;
    const selectedIndex = tabs.indexOf(filter);
    const selected = tabRefs.current.get(filter);
    if (!scroller || selectedIndex < 0 || !selected || scroller.clientWidth <= 0) return;

    const scrollerBounds = scroller.getBoundingClientRect();
    const selectedBounds = selected.getBoundingClientRect();
    const selectedStart = scroller.scrollLeft + selectedBounds.left - scrollerBounds.left;
    const selectedEnd = scroller.scrollLeft + selectedBounds.right - scrollerBounds.left;
    let left = scroller.scrollLeft;
    if (selectedStart < left) {
      left = selectedStart;
    } else if (selectedEnd > left + scroller.clientWidth) {
      left = selectedEnd - scroller.clientWidth;
    }

    const next = tabRefs.current.get(tabs[selectedIndex + 1]);
    if (next) {
      const nextBounds = next.getBoundingClientRect();
      const nextEnd = scroller.scrollLeft + nextBounds.right - scrollerBounds.left;
      if (nextEnd > left + scroller.clientWidth) {
        left = Math.min(nextEnd - scroller.clientWidth, selectedStart);
      }
    }

    const maxScrollLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const clampedLeft = Math.max(0, Math.min(maxScrollLeft, left));
    if (Math.abs(clampedLeft - scroller.scrollLeft) > 1) {
      scroller.scrollTo({ left: clampedLeft, behavior: 'smooth' });
    }
  }

  function selectScene(filter: string) {
    onFilterChange(filter);
    revealSelectedAndNext(filter);
  }

  return (
    <nav className="client-gallery-band border-b border-white/15" data-gallery-surface="scenes" aria-label="Gallery scenes">
      <div className="mx-auto flex max-w-[1364px] items-center">
        {hasSceneOverflow ? (
          <div className="mx-1 w-11 shrink-0">
            {canScrollLeft ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => scrollScenes(-1)}
                aria-label="Show previous scenes"
              >
                <ChevronLeft className="h-5 w-5" aria-hidden="true" />
              </Button>
            ) : null}
          </div>
        ) : null}
        <div
          ref={scrollRef}
          data-scene-scroll
          className="flex min-w-0 flex-1 gap-8 overflow-x-auto px-4 py-5 sm:gap-12 sm:px-6 sm:py-7 lg:gap-24"
        >
          {tabs.map((scene) => (
            <button
              key={scene}
              ref={(node) => {
                if (node) tabRefs.current.set(scene, node);
                else tabRefs.current.delete(scene);
              }}
              type="button"
              className={`shrink-0 border-b-2 pb-2 text-lg transition sm:text-2xl ${
                sceneFilter === scene ? 'border-white font-normal' : 'border-transparent font-light opacity-70 hover:opacity-100'
              }`}
              onClick={() => selectScene(scene)}
              aria-current={sceneFilter === scene ? 'page' : undefined}
            >
              {sceneLabel(scene)}
            </button>
          ))}
        </div>
        {hasSceneOverflow ? (
          <div className="mx-1 w-11 shrink-0">
            {canScrollRight ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => scrollScenes(1)}
                aria-label="Show more scenes"
              >
                <ChevronRight className="h-5 w-5" aria-hidden="true" />
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </nav>
  );
}

function SceneEmptyState({ sceneFilter }: { sceneFilter: string }) {
  return (
    <div className="flex min-h-48 flex-col items-center justify-center rounded-sm border border-dashed border-ink/20 bg-white/60 p-8 text-center">
      <p className="font-medium text-ink">
        {sceneFilter === ALL_SCENES
          ? 'No photos yet.'
          : sceneFilter === LOCAL_FAVOURITES_SCENE
            ? 'No favourite photos yet.'
            : `No loaded photos are assigned to ${sceneLabel(sceneFilter)}.`}
      </p>
    </div>
  );
}

function sceneLabel(scene: string): string {
  if (scene === ALL_SCENES) return ALL_PHOTOS_SCENE_LABEL;
  if (scene === PUBLIC_UNASSIGNED_SCENE) return OTHER_SCENE_LABEL;
  if (scene === LOCAL_FAVOURITES_SCENE) return FAVOURITES_SCENE_LABEL;
  return scene;
}

function filterPhotosByScene<TPhoto extends { photoId: string; scene?: string }>(
  photos: TPhoto[],
  sceneFilter: string,
  localFavouritePhotoIds: Set<string>
): TPhoto[] {
  if (sceneFilter === ALL_SCENES) {
    return photos;
  }
  if (sceneFilter === LOCAL_FAVOURITES_SCENE) {
    return photos.filter((photo) => localFavouritePhotoIds.has(photo.photoId));
  }
  return photos.filter((photo) => sceneFilter === PUBLIC_UNASSIGNED_SCENE ? !photo.scene : photo.scene === sceneFilter);
}

function PasscodeGate({
  isPending,
  exiting,
  error,
  onSubmit
}: {
  isPending: boolean;
  exiting: boolean;
  error: Error | null;
  onSubmit: (passcode: string) => void;
}) {
  const [passcode, setPasscode] = useState('');

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (exiting) return;
    onSubmit(passcode);
  }

  return (
    <form
      className={`client-gallery-passcode-card mx-auto grid w-full max-w-sm gap-4 rounded-sm border p-5 text-left shadow-soft sm:p-6 ${
        exiting ? 'client-gallery-passcode-card-exiting' : ''
      }`}
      data-passcode-card
      aria-hidden={exiting || undefined}
      onSubmit={handleSubmit}
    >
      <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-sm bg-smoke/80 text-ink/70">
        <Lock className="h-4 w-4" />
      </div>
      <Field label="Passcode">
        <Input value={passcode} onChange={(event) => setPasscode(event.target.value)} minLength={4} required autoFocus={!exiting} disabled={isPending} />
      </Field>
      {error ? <p className="text-sm text-clay">{error.message}</p> : null}
      <Button className="w-full" disabled={isPending}>
        {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
        <span>{isPending ? 'Unlocking' : 'Unlock'}</span>
      </Button>
    </form>
  );
}

function readLocalFavouriteState(key: string): LocalFavouriteState {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) ?? '') as LocalFavouriteState;
    if (!parsed || typeof parsed !== 'object' || !parsed.clients || typeof parsed.clients !== 'object') {
      return { clients: {} };
    }
    const anonymousPhotoIds = Array.isArray(parsed.anonymousPhotoIds)
      ? Array.from(new Set(parsed.anonymousPhotoIds.filter((photoId): photoId is string => typeof photoId === 'string')))
      : [];
    return { ...parsed, anonymousPhotoIds };
  } catch {
    return { clients: {} };
  }
}

function migrateStorageEntry(storage: Storage, previousKey: string, canonicalKey: string): void {
  if (previousKey === canonicalKey) return;
  const previousValue = storage.getItem(previousKey);
  if (previousValue !== null && storage.getItem(canonicalKey) === null) {
    storage.setItem(canonicalKey, previousValue);
  }
  storage.removeItem(previousKey);
}

function readFavouriteSession(key: string): FavouriteSessionState | null {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(key) ?? '') as FavouriteSessionState;
    return parsed && typeof parsed.clientKey === 'string' && typeof parsed.token === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

function createSubmissionId(): string {
  return typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function sameStringArray(left: string[], right: string[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

function isAuthenticationError(error: unknown): boolean {
  return Boolean(
    error &&
    typeof error === 'object' &&
    'status' in error &&
    ((error as { status?: unknown }).status === 401 || (error as { status?: unknown }).status === 403)
  );
}
